import type {
  PageResult,
  ReportCategory,
  ReportSummary,
} from "@hydeck/mdt-contracts";
import {
  createEffect,
  createResource,
  createSignal,
  For,
  Show,
} from "solid-js";
import { DumpDetail, DumpField } from "../components/DumpDetail";
import { DumpIcon } from "../components/DumpIcon";
import {
  dumpFilterQuery,
  DumpList,
  type DumpAdvancedSearchField,
} from "../components/DumpList";
import { ShareReportSection } from "../components/ShareReportSection";
import { HistoryModal } from "../components/HistoryModal";
import { RecordRelations } from "../components/RecordRelations";
import { ReportWorkflowPanel } from "../components/ReportWorkflowPanel";
import { RichTextEditor } from "../components/RichTextEditor";
import { runtimeConfig } from "../config";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";

export function ReportsPage() {
  const [search, setSearch] = createSignal("");
  const [advanced, setAdvanced] = createSignal<Record<string, string>>({});
  const [revision, setRevision] = createSignal(0);
  const [selected, setSelected] = createSignal<ReportSummary | null>(null);
  const [title, setTitle] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [categoryId, setCategoryId] = createSignal(1);
  const [isPublic, setIsPublic] = createSignal(false);
  const [sharedPublic, setSharedPublic] = createSignal(false);
  const [saving, setSaving] = createSignal(false);
  const [linkStatus, setLinkStatus] = createSignal("");
  const [bookmarked, setBookmarked] = createSignal(false);
  const [notice, setNotice] = createSignal("");
  const [historyOpen, setHistoryOpen] = createSignal(false);

  const [categories] = createResource(() =>
    apiRequest<ReportCategory[]>("/report-categories", {
      token: currentUser()?.accessToken,
    }),
  );
  const [reports] = createResource(
    () => [search(), JSON.stringify(advanced()), revision()] as const,
    ([query]) =>
      apiRequest<PageResult<ReportSummary>>("/reports", {
        token: currentUser()?.accessToken,
        query: { page: 1, limit: 50, search: query, ...dumpFilterQuery(advanced()) },
      }),
  );

  createEffect(() => {
    const report = selected();
    setTitle(report?.title ?? "");
    setDescription(report?.description ?? "");
    setCategoryId(report?.reportCategoryId ?? categories()?.[0]?.id ?? 1);
    setIsPublic(report?.isPublic ?? false);
    setSharedPublic(false);
    setLinkStatus("");
    setBookmarked(report?.isBookmarked ?? false);
    setNotice("");
  });

  const newReport = () => {
    const timestamp = new Date().toISOString();
    const category = categories()?.[0];
    setSelected({
      id: 0,
      reportCategoryId: category?.id ?? 1,
      title: "New Report",
      description: templateValue(category?.defaultTemplate),
      createdAt: timestamp,
      updatedAt: timestamp,
      createdBy: currentUser()?.id ?? 0,
      createdByName: `${currentUser()?.firstName ?? ""} ${currentUser()?.lastName ?? ""}`.trim(),
      isLocked: false,
      isPublic: false,
      tags: [],
      isBookmarked: false,
    });
  };

  const save = async () => {
    const report = selected();
    if (!report || !title().trim()) return;
    setSaving(true);
    try {
      const saved = await apiRequest<ReportSummary>(
        report.id
          ? `/reports/${report.reportCategoryId}/${report.id}`
          : "/reports",
        {
          method: report.id ? "PATCH" : "POST",
          token: currentUser()?.accessToken,
          body: {
            reportCategoryId: categoryId(),
            title: title(),
            // The dump stores the Quill Delta as JSON.  Normalize legacy
            // plain-text records once so autosave does not double-encode it.
            description: normalizeDescription(description()),
            isPublic: isPublic(),
          },
        },
      );
      setSelected(saved);
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const refreshReport = async () => {
    const report = selected();
    if (!report?.id) return;
    setSaving(true);
    try {
      setSelected(await apiRequest<ReportSummary>(
        `/reports/${report.reportCategoryId}/${report.id}`,
        { token: currentUser()?.accessToken },
      ));
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const copyPublicLink = async () => {
    const report = selected();
    if (!report?.id) return;
    setLinkStatus("Creating secure link...");
    try {
      const link = isPublic() || sharedPublic()
        ? publicRecordUrl(report.reportCategoryId, report.id)
        : await apiRequest<string>(
            `/reports/${report.reportCategoryId}/${report.id}/export`,
            { method: "POST", token: currentUser()?.accessToken },
          );
      await navigator.clipboard.writeText(link);
      setLinkStatus(
        isPublic() || sharedPublic()
          ? "Public record link copied"
          : "Secure public link copied (short-lived)",
      );
    } catch (reason) {
      setLinkStatus(reason instanceof Error ? reason.message : "Could not create link");
    }
  };

  const canShare = () =>
    hasPermission("reports.export") || hasPermission("reports.share");

  const canExport = () => {
    const report = selected();
    return !!report && (hasPermission("reports.export", String(report.reportCategoryId)) || hasPermission("reports.export"));
  };

  const exportReport = async () => {
    const report = selected();
    if (!report?.id || !canExport()) return;
    setSaving(true);
    try {
      const url = await apiRequest<string>(`/reports/${report.reportCategoryId}/${report.id}/export`, {
        method: "POST",
        token: currentUser()?.accessToken,
      });
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setSaving(false);
    }
  };

  const canUpdate = () => {
    const report = selected();
    return !!report && (hasPermission("reports.update", String(report.reportCategoryId)) || hasPermission("reports.update"));
  };

  const toggleLock = async () => {
    const report = selected();
    if (!report?.id || !canUpdate()) return;
    setSaving(true);
    try {
      const saved = await apiRequest<ReportSummary>(`/reports/${report.reportCategoryId}/${report.id}`, {
        method: "PATCH",
        token: currentUser()?.accessToken,
        body: { isLocked: !report.isLocked },
      });
      setSelected(saved);
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const toggleBookmark = async () => {
    const report = selected();
    if (!report?.id) return;
    setSaving(true);
    try {
      await apiRequest(`/bookmarks/report/${report.id}`, {
        method: bookmarked() ? "DELETE" : "POST",
        token: currentUser()?.accessToken,
      });
      setBookmarked((value) => !value);
    } finally {
      setSaving(false);
    }
  };

  const promote = async () => {
    const report = selected();
    if (!report?.id) return;
    setSaving(true);
    try {
      await apiRequest(`/reports/${report.reportCategoryId}/${report.id}/promote`, {
        method: "POST",
        token: currentUser()?.accessToken,
      });
      setNotice("Report promoted to an incident.");
    } finally {
      setSaving(false);
    }
  };

  const deleteReport = async () => {
    const report = selected();
    if (!report?.id || !hasPermission("reports.delete") || !window.confirm(`Delete report #${report.id}?`)) return;
    setSaving(true);
    try {
      await apiRequest(`/reports/${report.reportCategoryId}/${report.id}`, {
        method: "DELETE",
        token: currentUser()?.accessToken,
      });
      setSelected(null);
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const categoryName = (report: ReportSummary) =>
    categories()?.find(({ id }) => id === report.reportCategoryId)?.name ?? "";

  return (
    <>
    <Show
      when={selected()}
      fallback={
        <DumpList
          label="Reports"
          loading={reports.loading}
          search={search()}
          onSearch={setSearch}
          onAdd={hasPermission("reports.create") ? newReport : undefined}
          advancedValues={advanced()}
          onAdvancedApply={setAdvanced}
          advancedFields={reportAdvancedFields(categories() ?? [])}
          entries={(reports()?.items ?? []).map((report) => ({
            id: report.id,
            title: report.title,
            titleGrey: categoryName(report),
            description: report.description,
            icon: "bolo_docs",
            leftLabel: `ID: ${report.id}`,
            rightLabel: `${report.createdByName} - ${formatTimestamp(report.createdAt)}`,
            endLabel: (
              <ul class="ml-auto flex justify-end gap-1">
                <For each={report.tags.slice(0, 4)}>
                  {(tag) => (
                    <li class="flex max-h-12 min-h-8 items-center justify-center bg-[rgba(255,255,255,.11)] px-2">
                      <p class="text-sm">{tag}</p>
                    </li>
                  )}
                </For>
              </ul>
            ),
            raw: report,
          }))}
          onSelect={(entry) => setSelected(entry.raw as ReportSummary)}
        />
      }
    >
      {(report) => (
        <DumpDetail
          label={report().id ? `View Report #${report().id}` : "Create Report"}
          meta={
            <Show when={report().id > 0}>
              <span class="truncate text-xs text-white-55">
                {report().createdByName || "Unknown"} · {formatTimestamp(report().createdAt)}
              </span>
            </Show>
          }
          onBack={() => setSelected(null)}
          actions={
            <>
              <Show when={report().id > 0 && hasPermission("bookmarks.view")}>
                <button class="h-7 bg-blue-gradient px-2 text-xs" disabled={saving()} onClick={() => void toggleBookmark()}>
                  {bookmarked() ? "★ Saved" : "☆ Bookmark"}
                </button>
              </Show>
              <Show when={report().id > 0 && canUpdate()}>
                <button class="h-7 bg-white-11 px-2 text-xs" disabled={saving()} onClick={() => void toggleLock()}>
                  {report().isLocked ? "Unlock" : "Lock"}
                </button>
              </Show>
              <Show when={report().id > 0}>
                <button class="h-7 bg-white-11 px-2 text-xs" disabled={saving()} onClick={() => void refreshReport()}>
                  Refresh
                </button>
              </Show>
              <Show when={report().id > 0 && hasPermission("reports.view", String(report().reportCategoryId))}>
                <button class="h-7 bg-white-11 px-2 text-xs" disabled={saving()} onClick={() => setHistoryOpen(true)}>
                  History
                </button>
              </Show>
              <Show when={report().id > 0 && canExport()}>
                <button class="h-7 bg-blue-gradient px-2 text-xs" disabled={saving()} onClick={() => void exportReport()}>
                  Export
                </button>
              </Show>
              <Show when={report().id > 0 && canUpdate()}>
                <button class="h-7 bg-blue-gradient px-2 text-xs" disabled={saving()} onClick={() => void promote()}>
                  Promote
                </button>
              </Show>
              <Show when={report().id > 0 && hasPermission("reports.delete")}>
                <button class="h-7 bg-red-gradient px-2 text-xs" disabled={saving()} onClick={() => void deleteReport()}>
                  Delete
                </button>
              </Show>
              <Show when={report().id && canShare()}>
                <button
                  class="flex h-7 w-7 cursor-pointer items-center justify-center bg-blue-gradient transition-all duration-200 ease-in-out hover:drop-shadow-blue-gradient"
                  aria-label="Copy public record link"
                  onClick={() => void copyPublicLink()}
                >
                  <DumpIcon name="icon_copy_docs" />
                </button>
              </Show>
              <Show when={report().id && (isPublic() || sharedPublic()) && canShare()}>
                <a
                  class="flex h-7 items-center justify-center bg-green-gradient px-3 text-xs text-white transition-all duration-200 ease-in-out hover:drop-shadow-green-gradient"
                  href={publicRecordUrl(report().reportCategoryId, report().id)}
                  target="_blank"
                  rel="noreferrer"
                  title="View Public Record"
                >
                  View Public Record
                </a>
              </Show>
              <Show when={canUpdate() || !report().id}>
                <button
                  class="flex h-7 w-7 cursor-pointer items-center justify-center bg-green-gradient transition-all duration-200 ease-in-out hover:drop-shadow-green-gradient disabled:opacity-40"
                  disabled={saving()}
                  aria-label="Save report"
                  onClick={() => void save()}
                >
                  <DumpIcon name="icon_save_docs" />
                </button>
              </Show>
            </>
          }
        >
          <div class="flex h-full min-h-0 flex-col gap-4 p-3 lg:flex-row">
            <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
              <ul class="flex min-h-11 w-full flex-col gap-3">
                <DumpField label="Report ID" value={report().id} disabled />
                <li class="flex min-h-10 justify-between pl-4 leading-10">
                  <p>Category</p>
                  <select
                    class="w-[70%] bg-[rgba(255,255,255,.11)] px-3 text-left text-white"
                    value={categoryId()}
                    onChange={(event) => setCategoryId(Number(event.currentTarget.value))}
                  >
                    <For each={categories()}>
                      {(category) => <option class="text-black-54" value={category.id}>{category.name}</option>}
                    </For>
                  </select>
                </li>
                <DumpField label="Title" value={title()} onInput={setTitle} />
                <DumpField label="Created By" value={report().createdByName} disabled />
                <li class="flex min-h-10 items-center justify-between pl-4 leading-10">
                  <p>Public record</p>
                  <label class="flex w-[70%] items-center gap-3 bg-[rgba(255,255,255,.11)] px-3">
                    <input type="checkbox" checked={isPublic()} disabled={!hasPermission("reports.update") && !!report().id} onChange={(event) => setIsPublic(event.currentTarget.checked)} />
                    <span class="text-sm text-white-55">Allow civilians to view this report from its public URL</span>
                  </label>
                </li>
              </ul>
              <div class="flex min-h-0 flex-1 flex-col gap-2 pl-4">
                <p>Description</p>
                <RichTextEditor
                  value={description()}
                  resetKey={`${report().reportCategoryId}:${report().id}`}
                  roomName={report().id > 0 ? `report-${report().id}` : undefined}
                  disabled={report().isLocked || !canUpdate()}
                  onChange={setDescription}
                  onAutoSave={report().id > 0 ? save : undefined}
                />
                <Show when={linkStatus()}><p class="pl-4 text-sm text-cyan">{linkStatus()}</p></Show>
                <Show when={notice()}><p class="text-xs text-white-55">{notice()}</p></Show>
              </div>
            </div>
            <Show when={report().id > 0}>
              <aside class="w-full max-w-full overflow-y-auto pr-1 lg:max-w-[30%]">
                <RecordRelations
                  type="report"
                  item={report()}
                  onReload={async () => {
                    setSelected(await apiRequest<ReportSummary>(`/reports/${report().reportCategoryId}/${report().id}`, { token: currentUser()?.accessToken }));
                  }}
                />
                <ReportWorkflowPanel
                  report={report()}
                  category={categories()?.find((category) => category.id === report().reportCategoryId)}
                  onReload={async () => {
                    setSelected(await apiRequest<ReportSummary>(`/reports/${report().reportCategoryId}/${report().id}`, { token: currentUser()?.accessToken }));
                  }}
                />
                <ShareReportSection
                  endpoint={`/reports/${report().reportCategoryId}/${report().id}/share`}
                  reportType="reports"
                  permissionScope={String(report().reportCategoryId)}
                  onPublicChange={setSharedPublic}
                />
              </aside>
            </Show>
          </div>
        </DumpDetail>
      )}
    </Show>
      <HistoryModal
        kind="report"
        id={selected()?.id ?? 0}
        categoryId={selected()?.reportCategoryId}
        open={historyOpen() && (selected()?.id ?? 0) > 0}
        canRevert={canUpdate()}
        onClose={() => setHistoryOpen(false)}
        onRevert={(value) => {
          setDescription(value);
          setNotice("History version loaded. Save the report to apply it.");
          setHistoryOpen(false);
        }}
      />
    </>
  );
}

function templateValue(template: unknown): string {
  if (!template) return "";
  if (typeof template === "string") return template;
  try {
    return JSON.stringify(template);
  } catch {
    return "";
  }
}

function normalizeDescription(value: string): string {
  if (!value) return JSON.stringify({ ops: [] });
  try {
    return JSON.stringify(JSON.parse(value));
  } catch {
    return JSON.stringify({ ops: [{ insert: `${value}\n` }] });
  }
}

function formatTimestamp(value: string) {
  return new Date(value).toUTCString();
}

function publicRecordUrl(categoryId: number, reportId: number) {
  return `${runtimeConfig.publicOrigin}/public/reports/${categoryId}/${reportId}`;
}

function reportAdvancedFields(categories: ReportCategory[]): DumpAdvancedSearchField[] {
  return [
    {
      key: "createdBy",
      label: "Creator ID",
      type: "number" as const,
      placeholder: "User ID",
    },
    { key: "officers", label: "Officer IDs", placeholder: "Comma-separated IDs" },
    { key: "tags", label: "Tag ID", type: "number" as const, placeholder: "Tag ID" },
    { key: "citizens", label: "Citizen IDs", placeholder: "Comma-separated IDs" },
    {
      key: "reportCategoryId",
      label: "Category",
      type: "select" as const,
      options: categories.map((category) => ({
        value: String(category.id),
        label: category.name,
      })),
    },
    { key: "createdFrom", label: "Date From", type: "datetime-local" as const },
    { key: "createdTo", label: "Date To", type: "datetime-local" as const },
    {
      key: "sort",
      label: "Sort",
      type: "select" as const,
      options: [
        { value: "createdAt", label: "Created" },
        { value: "updatedAt", label: "Updated" },
        { value: "title", label: "Title" },
      ],
    },
    {
      key: "sortDirection",
      label: "Direction",
      type: "select" as const,
      options: [
        { value: "desc", label: "Newest first" },
        { value: "asc", label: "Oldest first" },
      ],
    },
  ];
}
